<template>
  <v-expansion-panels v-model="panel" class="ticket-personalization">
    <v-expansion-panel>
      <v-expansion-panel-title>
        <v-icon start>fal fa-user-tag</v-icon>
        <span class="font-weight-medium">Personalize Tickets</span>
        <v-tooltip
          v-if="!purchase.isPublic"
          location="top left"
          text="Ticket templates must be configured to show personalized names"
        >
          <template v-slot:activator="{ props }">
            <v-icon
              icon="fas fa-question-circle"
              class="ml-1"
              size="20"
              v-bind="props"
            />
          </template>
        </v-tooltip>
        <span class="text-caption ml-2">(optional)</span>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-alert type="info" variant="tonal" class="mb-4">
          Assign a name to each pass or ticket. A multi-day pass takes one name,
          used on every day it covers. If left blank, the purchaser's name will
          be used.
        </v-alert>

        <template v-for="(entry, index) in renderList" :key="entry.group.key">
          <div v-if="entry.header" class="section-header">
            {{ entry.header }}
          </div>

          <!-- A pass is a card so its days read as one unit; a lone ticket keeps the
               flat layout it always had. -->
          <div
            :class="isPassGroup(entry.group) ? 'pass-group' : 'ticket-item'"
            :data-group-key="entry.group.key"
          >
            <div class="group-header">
              <span
                :class="
                  isPassGroup(entry.group) ? 'group-name' : 'ticket-number'
                "
                >{{ entry.group.title }}</span
              >
              <v-chip
                v-if="entry.group.ordinalLabel"
                size="small"
                variant="outlined"
                class="ml-2"
              >
                {{ entry.group.ordinalLabel }}
              </v-chip>
              <v-chip
                v-if="entry.group.dateLabel"
                size="small"
                color="primary"
                variant="outlined"
                class="ml-2"
              >
                <v-icon start size="x-small">fal fa-calendar</v-icon>
                {{ dateChipText(entry.group) }}
              </v-chip>
              <v-chip
                v-if="entry.group.seatLabel"
                size="small"
                color="primary"
                variant="outlined"
                class="ml-2"
              >
                <v-icon start size="x-small">fal fa-chair</v-icon>
                {{ entry.group.seatLabel }}
              </v-chip>
              <v-chip
                v-if="entry.group.scannedCount > 0"
                size="small"
                color="warning"
                variant="outlined"
                class="ml-2"
              >
                <v-icon start size="x-small">fal fa-barcode-read</v-icon>
                {{ entry.group.scannedCount }} of
                {{ entry.group.dayCount }}
                {{ entry.group.dayCount === 1 ? "ticket" : "days" }} scanned
              </v-chip>
            </div>

            <v-row class="mt-2">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="names[entry.group.key].firstName"
                  label="First Name"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="getErrorMessages(entry.group, 'firstName')"
                  @change="saveTicketNames"
                  maxlength="50"
                  counter="50"
                  hide-details="auto"
                >
                  <template #prepend-inner>
                    <v-icon size="small">fal fa-user</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="names[entry.group.key].lastName"
                  label="Last Name"
                  variant="outlined"
                  density="comfortable"
                  :error-messages="getErrorMessages(entry.group, 'lastName')"
                  @change="saveTicketNames"
                  maxlength="50"
                  counter="50"
                  hide-details="auto"
                >
                  <template #prepend-inner>
                    <v-icon size="small">fal fa-user</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <div
              v-if="entry.group.dayCount > 1"
              class="group-note"
              :class="{ 'group-note--warning': entry.group.scannedCount > 0 }"
            >
              <v-icon size="x-small" class="mr-1">
                {{
                  entry.group.scannedCount > 0
                    ? "fal fa-exclamation-triangle"
                    : "fal fa-check-circle"
                }}
              </v-icon>
              <span v-if="entry.group.scannedCount > 0">
                Changing this name updates all {{ entry.group.dayCount }} days,
                including {{ entry.group.scannedCount }} already scanned.
              </span>
              <span v-else>
                Applies to all {{ entry.group.dayCount }} days of this pass.
              </span>
            </div>

            <div v-if="entry.group.hasDivergentNames" class="group-note">
              <v-icon size="x-small" class="mr-1">fal fa-info-circle</v-icon>
              This pass currently has different names on different days. Saving
              will apply the name above to every day.
            </div>
          </div>

          <v-divider
            v-if="!isPassGroup(entry.group) && index < renderList.length - 1"
            class="my-4"
          />
        </template>

        <v-alert
          v-if="saveStatus === 'saving'"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
          Saving...
        </v-alert>

        <v-alert
          v-if="saveStatus === 'success'"
          type="success"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          <v-icon start size="small">fal fa-check</v-icon>
          Names saved successfully!
        </v-alert>

        <v-alert
          v-if="saveStatus === 'error'"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          <v-icon start size="small">fal fa-exclamation-triangle</v-icon>
          {{ errorMessage }}
        </v-alert>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import {
  buildPersonalizationGroups,
  expandGroupNames,
  shouldShowSectionHeaders,
  type PersonalizationGroup,
} from "@common/purchase/ticket-personalization-groups";
import useExchange from "@common/composables/useExchange";

interface Props {
  purchase: TicketPurchaseDtoViewModel;
  service: any; // IPublicPurchaseService or IPrivatePurchaseService
  adminMode?: boolean;
}

const props = defineProps<Props>();

const panel = ref<number | undefined>(undefined);
const saveStatus = ref<"idle" | "saving" | "success" | "error">("idle");
const errorMessage = ref("");
const validationErrors = ref<
  Map<string, { firstName?: string; lastName?: string }>
>(new Map());

// Mirrors the server-side check in UpdateTicketNames so the error surfaces before the round-trip.
const validNameRegex = /^[a-zA-Z0-9\s\-']+$/;

// A multi-day pass is one Ticket per covered date; the panel works in passes sold, not tickets.
const groups = computed<PersonalizationGroup[]>(() =>
  buildPersonalizationGroups(
    (props.purchase.tickets ?? []) as any[],
    (props.purchase.eventSeries?.passTypes ?? []) as any[],
  ),
);

const names = ref<Record<string, { firstName: string; lastName: string }>>({});

// #3492: an exchange is created with blank names, so the patron's personalization carries over
// from the purchase it replaces. Group-to-group, not ticket-to-ticket — a pass is one name over N
// tickets, so matching per ticket would give a single pass three different patrons' names.
const { originalPurchase } = useExchange(props.purchase);

const originalGroups = computed<PersonalizationGroup[]>(() => {
  const original = originalPurchase.value;
  if (!original) return [];

  // While the exchange is a cart the server names the exact rows it replaces, so preview only
  // those: a partial exchange must not offer a patron it is not taking over, and the preview has
  // to agree with what CarryOverPersonalizedNames stores on completion.
  const pendingIds = props.purchase.originalPurchase?.ticketIds ?? [];
  const tickets = (original.tickets ?? []) as any[];

  const available = pendingIds.length
    ? tickets.filter((t) => pendingIds.includes(t.ticketId))
    : // Completed, so that list is gone: fall back to skipping rows already claimed by some other
      // exchange or refund. Rows claimed by this purchase are the ones it replaced, so keep them.
      tickets.filter(
        (t) =>
          !t.relatedPurchase ||
          t.relatedPurchase.publicId === props.purchase.publicId,
      );

  return buildPersonalizationGroups(
    available,
    (original.eventSeries?.passTypes ?? []) as any[],
  );
});

// shift() advances even when the group already has a name, so the two lists stay positionally
// aligned and a later group meets its own counterpart rather than the previous group's.
const seededNames = computed(() => {
  const carryOver = [...originalGroups.value];
  const seeded: Record<string, { firstName: string; lastName: string }> = {};

  for (const group of groups.value) {
    const original = carryOver.shift();
    seeded[group.key] = {
      firstName: group.assignedFirstName || original?.assignedFirstName || "",
      lastName: group.assignedLastName || original?.assignedLastName || "",
    };
  }

  return seeded;
});

// Keyed on the seed values, not the group objects: an unrelated reload (a day scanned, a price
// refresh) must not disturb the fields, while a save — or the carry-over landing on its own
// round-trip — must still reach them.
const persistedNamesSignature = computed(() =>
  Object.entries(seededNames.value)
    .map(([key, n]) => `${key}=${n.firstName}|${n.lastName}`)
    .join(","),
);

// The seed can change after the agent has started typing, since the carry-over arrives on its own
// round-trip. Remembering what was last written is what tells their edit apart from a stale field.
let appliedSeed: Record<string, { firstName: string; lastName: string }> = {};

watch(
  persistedNamesSignature,
  () => {
    const next = seededNames.value;
    const merged: Record<string, { firstName: string; lastName: string }> = {};

    for (const [key, seed] of Object.entries(next)) {
      const current = names.value[key];
      const applied = appliedSeed[key];
      // Per group rather than per field, so a half-filled pass never pairs a typed first name
      // with a carried-over last name.
      const editedByHand =
        current != null &&
        applied != null &&
        (current.firstName !== applied.firstName ||
          current.lastName !== applied.lastName);

      merged[key] = editedByHand ? current : { ...seed };
    }

    appliedSeed = next;
    names.value = merged;
  },
  { immediate: true },
);

const renderList = computed(() => {
  const showHeaders = shouldShowSectionHeaders(groups.value);
  let lastKind: string | null = null;

  return groups.value.map((group) => {
    let header: string | null = null;
    if (showHeaders && group.kind !== lastKind) {
      if (group.kind === "multiDayPass") header = "Multi-Day Passes";
      else if (group.kind === "singleDayPass") header = "Single-Day Passes";
    }
    lastKind = group.kind;
    return { group, header };
  });
});

function isPassGroup(group: PersonalizationGroup): boolean {
  return group.kind !== "ticket";
}

function dateChipText(group: PersonalizationGroup): string {
  if (group.dayCount <= 1) return group.dateLabel;
  return `${group.dateLabel} · ${group.dayCount} days`;
}

function validateName(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > 50) return "Maximum 50 characters";
  if (!validNameRegex.test(trimmed)) {
    return "Only letters, numbers, spaces, hyphens, and apostrophes allowed";
  }
  return undefined;
}

function validateGroup(group: PersonalizationGroup): {
  firstName?: string;
  lastName?: string;
} {
  const value = names.value[group.key] ?? { firstName: "", lastName: "" };
  const errors: { firstName?: string; lastName?: string } = {};

  const firstNameError = validateName(value.firstName);
  if (firstNameError) errors.firstName = firstNameError;

  const lastNameError = validateName(value.lastName);
  if (lastNameError) errors.lastName = lastNameError;

  return errors;
}

function getErrorMessages(
  group: PersonalizationGroup,
  field: "firstName" | "lastName",
): string[] {
  const errors = validationErrors.value.get(group.key);
  if (errors && errors[field]) {
    return [errors[field]!];
  }
  return [];
}

async function saveTicketNames() {
  // Validate all groups
  validationErrors.value.clear();
  let hasErrors = false;

  for (const group of groups.value) {
    const errors = validateGroup(group);
    if (Object.keys(errors).length > 0) {
      console.warn("[TicketPersonalization] Validation errors for group", {
        groupKey: group.key,
        ticketIds: group.ticketIds,
        errors,
      });
      validationErrors.value.set(group.key, errors);
      hasErrors = true;
    }
  }

  if (hasErrors) {
    console.error("[TicketPersonalization] Validation failed", {
      errors: Array.from(validationErrors.value.entries()),
    });
    saveStatus.value = "error";
    errorMessage.value = "Please correct the validation errors.";
    return;
  }

  try {
    saveStatus.value = "saving";

    // One name per group fans out to every ticket in it, so a multi-day pass gets the same
    // name on all of its days. The endpoint contract is unchanged: a flat per-ticket list.
    const ticketNames = expandGroupNames(
      groups.value,
      (group) => names.value[group.key] ?? { firstName: "", lastName: "" },
    );

    // Call the service using the args pattern (like addMultipleTickets)
    const methodName = props.adminMode
      ? "updateTicketNamesAdmin"
      : "updateTicketNames";
    const apiState = props.service[methodName];
    if (!apiState) {
      saveStatus.value = "error";
      errorMessage.value = "Save method not available on service.";
      return;
    }
    apiState.args = {
      publicId: props.purchase.publicId,
      ticketNames: ticketNames,
    };

    // Invoke the API call
    await apiState.invokeWithArgs();

    // Use apiState.wasSuccessful directly
    const wasSuccessful = apiState.wasSuccessful === true;
    const message = apiState.message;

    if (wasSuccessful) {
      saveStatus.value = "success";

      // Clear status after 2 seconds
      setTimeout(() => {
        saveStatus.value = "idle";
      }, 2000);
    } else {
      console.error("[TicketPersonalization] Save failed", {
        message: message,
        apiState: apiState,
        apiStateResult: apiState.result,
      });
      saveStatus.value = "error";
      errorMessage.value = message || "Error saving names.";

      // Clear error status after 5 seconds
      setTimeout(() => {
        saveStatus.value = "idle";
      }, 5000);
    }
  } catch (error) {
    console.error("[TicketPersonalization] Exception occurred", error);
    saveStatus.value = "error";
    errorMessage.value = "Error saving. Please try again.";

    setTimeout(() => {
      saveStatus.value = "idle";
    }, 5000);
  }
}
</script>

<style scoped>
.ticket-personalization {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.ticket-item {
  padding: 0.5rem 0;
}

.pass-group {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  padding: 0.75rem 1rem 1rem;
  margin-bottom: 0.875rem;
}

.section-header {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.65;
  margin: 1rem 0 0.5rem;
}

.section-header:first-child {
  margin-top: 0;
}

.group-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.ticket-number,
.group-name {
  font-weight: 600;
  font-size: 1rem;
  color: rgb(var(--v-theme-primary));
}

.group-note {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  opacity: 0.75;
  margin-top: 0.75rem;
}

.group-note--warning {
  color: rgb(var(--v-theme-warning));
  opacity: 1;
}

/* Mobile: stack fields vertically */
@media (max-width: 600px) {
  .v-row {
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
